/**
 * 浏览与编辑：**按课本 → 年级学期 → 课文**看图，并直接改拼音 / 增删字词。
 *
 * ## 字是**网格卡片**，点开才是操作（2026-10-18 用户要求）
 *
 * 用户原话：
 *
 * > "一边是以椭圆框显示，一行显示一个字。另一边是圆角框，显示得比较好，一行显示
 * >  几个字。所以，**录入汉字这个模块，显示汉字的方式比较好**……把这一刻显示汉字的
 * >  方法，替换掉浏览查询模块里显示汉字的方式……**每一个汉字，你不需要显示它是
 * >  属于哪个表**……但是如果它是多音字，你最好用颜色（也就是框框的底色）去表示它，
 * >  查出来之后，**我点某一个框框，就要弹出一个浮动窗来。具体所有的操作都在这个
 * >  框框里面进行**。"
 *
 * 所以「生字」那一块是 `grid-cols-3 / sm:6 / lg:8` 的卡片网格（`HanziCard`），
 * 卡片里只有**拼音（上）+ 字（下）**，**没有**收录徽章、**没有**按钮：
 * 点整张卡 → `onOpenHanzi` → `HanziReadingsModal`（读音与造词、隐藏不常用的音、
 * 改拼音、标注多音字、编辑、删除全在里面）。
 * ⚠️ 底色**只给已确认标注为多音字**的字（`polyphonic === true`）；
 *    拼音库里的多读音（`readings.length > 1`）**不上色**。
 *
 * ## 为什么词还是"一行一个"
 *
 * 用户点名改的是「汉字」那一块。词语每一行要放整词拼音、类别、来源等好几项信息，
 * 挤进网格卡片里读不清，所以**词仍用 `ItemRow`**（拼音仍可点开就地编辑）。
 *
 * ## 「0 / 拿不到」的展示口径
 *
 * 后端所有列表端点都有 `total`，而**课本列表没有**（每个课本自己带三个计数）。
 * 所以：
 *   · `total` 拿不到 → 显示「—」并给出"数据没拿到"的说明，**不显示 0**；
 *   · 列表字段拿不到（解码成 `null`）→ 显示"没拿到这份数据"，**不是"一条都没有"**；
 *   · 真 0 条 → 显示"还没有…"的引导文案。
 * 这三条由 `describeListState()` 判定（有单测）。
 *
 * ## 2026-10-18：筛选**全都没选**时不列字词（用户原话："这里没有选择任何选项，
 * 下面就不应该出现字或词的列表"）
 *
 * 口径与判据**全部**在 `lib/dictationFilters.ts`。这里只负责渲染：
 *   · 未选（**已生效**那套没条件）→ **只**渲染筛选卡，**不生字词卡片**；
 *   · 已选 → 与改动前**逐字相同**（列表、计数、点拼音纠音一行未动）。
 *
 * ⚠️ 未选时**请求本身就没发**（`enabled` 判据在 `DictationWorkspace` 里）。
 * ⚠️ 家长端用的是**同一个面板**：没有第二套分支（家长与管理员只差 `canEdit`
 *    控制的那几个写入口）。
 *
 * ## 2026-10-18（本轮）：「先选、再查询」
 *
 * 用户原话："选好所有条件后，点查询按钮才出结果，不要选一个条件就刷新一个"。
 * 所以这个面板收到**两套**筛选：
 *   · `filter` —— **待生效**：下拉 / 勾选框显示的当前值。改它**不发请求**；
 *   · `appliedFilter` —— **已生效**：列表里的数据就是按它查的。只有点「查询」
 *     （或回车）才由 `DictationWorkspace` 把前者升成后者。
 *
 * 界面上由此多/少两样东西：
 *   1. 筛选卡底部一条**查询条**（提示 + 「重置」+ 「查询」），按钮在"没有可查条件"时
 *      禁用（**不再解释为什么**）；有未生效改动时按钮上亮一个小圆点、旁边有琥珀色提示；
 *   2. 回车 = 点「查询」（桌面端）。
 *
 * ## 2026-10-18（本轮后半）：**删掉两块界面**（用户圈红，"都不需要"）
 *
 *   1. 查询条左侧那句禁用原因 —— 整句删除；「查询」按钮**仍然**在没条件时禁用，
 *      但旁边**不写**"为什么禁用"（⛔ 也不许换一句话补上，那等于没删）；
 *   2. 未选时的那张引导卡（标题 + 正文 + 三个快捷按钮）—— **整张卡片连同按钮一起删**。
 *      ⛔ 不要保留替代品（例如换个样子的快捷入口）：删掉"点一下就能选好"这个便利，
 *      是用户明确要求的。
 *
 * ⚠️ 所以"没选条件"时页面上**只剩筛选卡**（含灰掉的「查询」按钮）——
 *    这是用户要的效果，**不要**再补一句提示。
 *
 * ⚠️ 「新增生字 / 新增词语」的启用判据用**已生效**那本课本（`appliedFilter`）：
 *    新增会写进那本书，用待生效的值会让字落到一本还没查过的书里。
 *
 * ## 2026-10-19：排序 / 按表分块 / 补「词语表」
 *
 * 用户原话（验收标准）：
 *   · 排序："按我当时粘贴录入的顺序显示……你现在是打乱了" ⇒ 排序在后端
 *     （`sort_order, created_at, id` 全序），这里**不重排**，只按拿到的顺序渲染；
 *   · 分块："识字表和写字表里只有字……词语表里只有词语" ⇒ `browseBlocks()` 决定
 *     **渲染哪一块**，另一块连卡片都不进 DOM（不是 CSS 隐藏）；
 *   · 下拉："「在哪张表上」里怎么没有「词语表」" ⇒ 补上 `cibiao`（后端既有枚举）。
 *
 * ## ⚠️ 2026-10-20：**取消 100 条上限，一次全部显示**（分页已删）
 *
 * 用户原话：
 *
 * > "查询出那些字的列表之后，不是每 100 个显示一批嘛，我觉得老是去点那 100 个
 * >  也挺麻烦的，你就**一次性全部显示**吧，有多少个就显示多少个，**取消那个 100 的限制**。"
 *
 * 所以本文件里：
 *   · 没有 `hanziShown` / `wordShown` / `BROWSE_PAGE_SIZE` / 「再显示 100 个」；
 *   · 也没有 `revealIndex`（那是"跳转目标在第 100 个之后要先展开"的旧逻辑）——
 *     现在所有卡片都在 DOM 里，跳转直接滚；
 *   · 「已显示 100 / 共 280 个」整行删掉，只留一句总数「共 280 个」
 *     （`lib/dictationBrowsePaging.ts` 的 `describeBrowseTotal`，词表同一条口径）；
 *   · **性能**：**没有**用 `content-visibility: auto`。它会把屏幕外条目的尺寸变成
 *     `contain-intrinsic-size` 的**估计值**，于是「跳到表尾」与"跳到某个字卡"的落点
 *     都会偏（CSSWG 直到 2024 年才把 `scrollIntoView` 补进 "relevant to the user"，
 *     而且滚动途中的兄弟节点尺寸仍可能变，见 csswg-drafts#9337）。本项目这一屏的
 *     跳转刚需"落点准"，所以选择**老老实实全渲染**：真实数据量是一册 ~280、全站
 *     几百到几千，每一行只是一个 `<button>` + 两三个 `<span>`（没有图片 / 图表），
 *     一次渲染完全够用；硬上限由 `DICTATION_BROWSE_LIMIT_MAX`（20000）兜住，
 *     真超过时列表末尾会明说"服务器这次只返回了 N 条"。
 *
 * ## 2026-10-20（同一轮）：字表 / 词表的「表头 ↔ 表尾」按钮
 *
 * 用户原话："在**字表的上面加一个按钮，直接跳转到表尾**；在**表尾那里加一个按钮，
 * 跳转到表头**。"
 *
 *   · 列表上方：「跳到表尾 ↓」；列表末尾：「跳到表头 ↑」；
 *   · 只在这一屏有（⛔ 导入预览不加）；
 *   · **始终显示**（不按"超过一屏"才出现 —— 理由见 `BrowseScrollJumps.tsx`）；
 *   · 滚动是 `window.scrollTo`（平滑；reduced-motion 时瞬时），实现在
 *     `BrowseScrollJumps.tsx` + `lib/browseScrollJumps.ts`。
 *
 * ⚠️ 计数与「新增生字 / 新增词语」按钮都长在各自卡片里，所以"只算当前块"是
 *    `browseBlocks()` 的自然结果，没有第二套开关。
 */
import { Fragment, useRef, useState, type ReactNode, type RefObject } from 'react';
import { Badge, Card, CardHeader, EmptyState, Skeleton } from '../ui/Card';
import { Button } from '../ui/Button';
import { Select } from '../ui/Input';
import { IconChevronDown, IconPencil, IconPlus, IconTrash } from '../ui/Icons';
import { gradeLabel, publisherDisplayNameOf } from '@fht/shared';
import { cn } from '../../lib/utils';
import {
  BROWSE_FILTER_RULE_HINT,
  BROWSE_QUERY_BUTTON_LABEL,
  BROWSE_RESET_BUTTON_LABEL,
  POLYPHONIC_FILTER_HINT,
  POLYPHONIC_FILTER_LABEL,
  browseBlocks,
  describeBrowseQuery,
  describeCreateTargetGap,
  describeFilterVolumeGap,
  findFilterVolume,
  gradeSelectOptions,
  isBrowseQueryShortcut,
  isFilterActive,
  publisherSelectOptions,
  type BrowseFilterState,
} from '../../lib/dictationFilters';
import { describeBrowseTotal } from '../../lib/dictationBrowsePaging';
import {
  browseFilterGridClass,
  browseFilterKeys,
  lessonsUiEnabled,
  type BrowseFilterKey,
} from '../../lib/dictationLessonsUi';
import {
  countLabel,
  describeListState,
  semesterLabel,
  wordCategoryLabel,
} from '../../lib/api/dictation';
import type { HanziView, LessonView, TextbookView, WordView } from '../../lib/api/dictation';
import { PinyinEditor } from './PinyinEditor';
import { HANZI_GRID_CLASS, HanziCard } from './HanziCard';
import { BrowseScrollJumps } from './BrowseScrollJumps';
import { hanziRowKey } from './hanziJump';
import { describeBrowseLineGroups, groupBrowseHanziByLine } from './browseLineGroups';

// ---------------------------------------------------------------- 小部件

/** 拼音单元格：正常展示，点一下变成编辑框。 */
function PinyinCell({
  kind,
  text,
  pinyin,
  pinyinAuto,
  overridden,
  alternates,
  editing,
  saving,
  error,
  onStartEdit,
  onSave,
  onCancel,
}: {
  kind: 'hanzi' | 'word';
  text: string;
  pinyin: string;
  pinyinAuto: string;
  overridden: boolean;
  alternates?: string[];
  editing: boolean;
  saving: boolean;
  error: string | null;
  onStartEdit: () => void;
  onSave: (value: string) => void;
  onCancel: () => void;
}) {
  if (editing) {
    return (
      <div className="mb-1">
        <PinyinEditor
          kind={kind}
          text={text}
          pinyin={pinyin}
          pinyinAuto={pinyinAuto}
          overridden={overridden}
          {...(alternates ? { alternates } : {})}
          saving={saving}
          error={error}
          onSave={onSave}
          onCancel={onCancel}
        />
      </div>
    );
  }

  const hasPinyin = pinyin !== '';
  return (
    <button
      type="button"
      onClick={onStartEdit}
      title={
        overridden
          ? `人工改过的读音（自动值是 ${pinyinAuto === '' ? '空' : pinyinAuto}）—— 点一下改`
          : `点一下改读音（自动值 ${pinyinAuto === '' ? '空' : pinyinAuto}）`
      }
      className={cn(
        // ⚠️ 2026-10-17：拼音现在排在这一行的**最上面**（见 `ItemRow`），所以间距从
        //    `mt-1`（原来在字下面，要往上撑开）改成 `mb-0.5`（在字上面，要往下撑开）。
        'mb-0.5 inline-flex max-w-full items-center gap-1 rounded-lg px-1.5 py-0.5 text-left font-mono',
        'hover:bg-brand-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-200',
        overridden ? 'bg-amber-50 text-amber-800' : 'text-slate-500',
      )}
    >
      <span className="min-w-0 truncate">
        {/* 空串是一个**真实状态**（内置字表里没有这个字），要说出来，不能留白 */}
        {hasPinyin ? pinyin : '（没有读音，点这里填）'}
      </span>
      {overridden && (
        <span className="shrink-0 rounded bg-amber-200/70 px-1 text-[11px] font-sans font-medium text-amber-900">
          改过
        </span>
      )}
      {!overridden && <IconPencil size={12} />}
    </button>
  );
}

/** 一行字/词的公共骨架（现在**只有词**用它：字是网格里的卡片，见下面的 `HanziCard`）。 */
function ItemRow({
  primary,
  pinyinNode,
  meta,
  badges,
  onEdit,
  onDelete,
}: {
  primary: string;
  pinyinNode: ReactNode;
  meta: string;
  badges: ReactNode;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <li className="flex flex-wrap items-start justify-between gap-2 rounded-xl border border-slate-200 p-3">
      {/* 可伸缩列：`min-w-0` 是既有约定（窄屏让内容先让位，别挤压按钮列） */}
      <div className="min-w-0 flex-1">
        {/*
          ⚠️ 2026-10-17（用户要求）：**拼音在上、字在下**（语文课本的排法）。
            改造前是"字 → 拼音 → 备注"，现在是"拼音 → 字 + 徽章 → 备注"。
            只换了先后：字的 `text-lg font-bold` 一个字都没改（视觉主体仍是字），
            拼音那枚按钮仍然点一下就地出编辑框（`PinyinCell`，行为一行未动）。
        */}
        {pinyinNode}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-lg font-bold text-slate-900">{primary}</span>
          {badges}
        </div>
        <p className="mt-1 text-xs text-slate-500">{meta}</p>
      </div>
      {/* 按钮列不参与收缩 */}
      <div className="flex shrink-0 items-center gap-2">
        <Button
          size="icon"
          variant="secondary"
          aria-label={`编辑 ${primary}`}
          title="编辑"
          onClick={onEdit}
          icon={<IconPencil size={18} />}
        />
        <Button
          size="icon"
          variant="danger"
          aria-label={`删除 ${primary}`}
          title="删除"
          onClick={onDelete}
          icon={<IconTrash size={18} />}
        />
      </div>
    </li>
  );
}

/**
 * **一个字卡**（「浏览与编辑」的字网格里那一格）。
 *
 * ## 为什么是卡片 + 网格（用户原话）
 *
 * > "一边是以椭圆框显示，一行显示一个字。另一边是圆角框，显示得比较好，
 * >  一行显示几个字。所以，**录入汉字这个模块，显示汉字的方式比较好**。"
 *
 * ## ⚠️ 2026-10-18（本轮）：卡片本体搬去了 `./HanziCard.tsx`
 *
 * 家长端「按书看字表」恢复那块字卡之后，**两处必须长得一模一样**
 * （同一套列数、同一套底色、同一套"点开浮窗"语义）。各写一份迟早漂移，
 * 所以卡片与列数只有那一份实现；这里只负责把它排进网格。
 *
 * 它仍然是「浏览与编辑」那一屏的卡片：
 *   · **拼音在上、字在下**（语文课本的排法，2026-10-17 已定）；
 *   · **不再显示"属于哪张表"**（用户："我查的时候就是通过哪个表去查的"）；
 *   · **只有已确认标注为多音字**（`polyphonic === true`）才上底色 ——
 *     ⛔ 技术层多读音（`readings.length > 1`）**不上色**；
 *   · 框里**不挤按钮**：所有操作都在点开之后的浮窗里（`HanziReadingsModal`）。
 */

// ---------------------------------------------------------------- 未选时（**只剩筛选卡**）

/**
 * ⛔ **这里原来有一张空态引导卡（标题 + 正文 + 三个快捷按钮）**，
 *    2026-10-18 本轮按用户要求**整张删除**（连同 `quickBrowseChips()` 那三个按钮）。
 *
 * ⚠️ 所以"没选条件"时页面上**只剩筛选卡**（含灰掉的「查询」按钮）：
 *    **不要**再补一句提示、也**不要**换个样子的快捷入口 —— 那等于没删。
 * ⚠️ 「查询」按钮在没条件时**仍然禁用**（`describeBrowseQuery().canQuery`），
 *    只是旁边不再写"为什么禁用"。
 */

// ---------------------------------------------------------------- 列表

/**
 * 列表末尾那一条：**只有一个总数**（「共 280 个」）+「跳到表头 ↑」。
 *
 * ## ⚠️ 2026-10-20：分页那行已删（用户改主意了）
 *
 * 上一版这里是「已显示 100 / 共 280 个」+「再显示 100 个」。用户原话：
 *
 * > "你就**一次性全部显示**吧，有多少个就显示多少个，**取消那个 100 的限制**。……
 * >  但**保留一个总数**（例如「共 280 个」）。"
 *
 * 所以现在：所有条目一次渲染（上面的列表不再 `slice`），这里只报**总数**；
 * 取不全时（超过 `DICTATION_BROWSE_LIMIT_MAX` = 20000）用琥珀色那句**明说**
 * "服务器这次只返回了 N 条"，不给一个点了没反应的按钮。
 *
 * ⚠️ 「跳到表头 ↑」就在这条的右边（表尾），文案与滚动见 `BrowseScrollJumps.tsx`。
 */
function BrowseListFooter({
  total,
  available,
  listRef,
}: {
  total: number | null;
  available: number;
  listRef: RefObject<HTMLElement | null>;
}) {
  const view = describeBrowseTotal(total, available);
  return (
    <div className="mt-3 flex flex-col gap-2 border-t border-slate-100 pt-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className="text-xs text-slate-500">{view.label}</p>
        {view.truncatedLabel !== null && (
          <p className="mt-1 text-xs leading-relaxed text-amber-700">{view.truncatedLabel}</p>
        )}
      </div>
      <BrowseScrollJumps listRef={listRef} edge="top" />
    </div>
  );
}

export interface DictationItemsPanelProps {
  textbooks: TextbookView[] | null;
  textbooksMissing: boolean;
  lessons: LessonView[] | null;
  lessonsTotal: number | null;
  lessonsMissing: boolean;
  hanzi: HanziView[] | null;
  hanziTotal: number | null;
  hanziMissing: boolean;
  words: WordView[] | null;
  wordsTotal: number | null;
  wordsMissing: boolean;
  loading: boolean;
  canEdit: boolean;
  /**
   * **待生效**的筛选（下拉 / 勾选框显示它；改它不发请求）。
   *
   * 由页面持有：切 tab 不丢；**不**写进 localStorage（存的是 `appliedFilter`）。
   */
  filter: BrowseFilterState;
  /** **已生效**的筛选：列表数据、本机记忆、"新增"落到哪本书，全都以它为准。 */
  appliedFilter: BrowseFilterState;
  /** 改**待生效**那套（下拉 / 勾选框 / 快捷按钮 / 重置都走这里）。 */
  onFilter: (patch: Partial<BrowseFilterState>) => void;
  /** 点「查询」/ 按回车：把待生效那套升成已生效（**唯一**下发请求的入口）。 */
  onQuery: () => void;
  /** 「重置」：只清待生效那套，**不查**、也不动已生效的那套。 */
  onResetFilter: () => void;
  /** 正在编辑拼音的那一条（`null` = 没有）。 */
  pinyinEdit: { kind: 'hanzi' | 'word'; id: string } | null;
  pinyinSaving: boolean;
  pinyinError: string | null;
  onStartPinyin: (kind: 'hanzi' | 'word', id: string) => void;
  onSavePinyin: (kind: 'hanzi' | 'word', item: HanziView | WordView, value: string) => void;
  onCancelPinyin: () => void;
  /**
   * **点一个字卡 → 打开浮窗**（读音与造词 / 改拼音 / 标为多音字 / 删除）。
   *
   * ⚠️ 2026-10-18（本轮）：字卡上**不再挤按钮**（用户："所有的操作都在这个框框里面
   *    进行"）。所以「标为多音字 / 组词 / 编辑 / 删除」四个动作全搬进了浮窗
   *    （`HanziReadingsModal`），这里只留一个"打开它"的回调。
   * ⚠️ 两种模式都传：家长点开也能看（只是浮窗里没有写入口）。
   * ⚠️ 第二个参数是**这张卡自己的跳转键**（`hanziRowKey`）：页面要按它把"当前行"
   *    从「还出现在」里排除掉（不能只靠 `item.listingId`，见 `hanziJump.ts`）。
   */
  onOpenHanzi: (item: HanziView, cardKey?: string) => void;
  onCreateLesson: () => void;
  onEditLesson: (lesson: LessonView) => void;
  onDeleteLesson: (lesson: LessonView) => void;
  onCreateHanzi: () => void;
  onCreateWord: () => void;
  onEditItem: (kind: 'hanzi' | 'word', item: HanziView | WordView) => void;
  onDeleteItem: (kind: 'hanzi' | 'word', item: HanziView | WordView) => void;

  // ---------------------------------------------------------------- 同一个字互相跳转（可选）
  /**
   * 把每张字卡的 DOM 登记给跳转器（`useHanziJump().register(key)`）。
   *
   * ⚠️ 与导入预览**同一份实现**（`hanziJump.ts`）：页面持有 hook，这里只负责接线。
   *    不传 ⇒ 行为与以前一个字都不差（「按书看字表」/ 既有测试）。
   */
  registerCard?: ((key: string) => (element: HTMLElement | null) => void) | undefined;
  /** 正在短暂高亮的那张卡（`useHanziJump().activeKey`）。 */
  highlightedKey?: string | null;
}

export function DictationItemsPanel(props: DictationItemsPanelProps) {
  const {
    textbooks,
    textbooksMissing,
    lessons,
    lessonsTotal,
    lessonsMissing,
    hanzi,
    hanziTotal,
    hanziMissing,
    words,
    wordsTotal,
    wordsMissing,
    loading,
    canEdit,
    filter,
    appliedFilter,
    onFilter,
    onQuery,
    onResetFilter,
  } = props;

  const [lessonsOpen, setLessonsOpen] = useState(false);

  /**
   * 两个字 / 词列表的 DOM（给「跳到表尾 ↓ / 跳到表头 ↑」量位置用）。
   *
   * ⚠️ 挂在列表容器上即可：按钮要的是"整份列表的顶 / 底"，
   *    实测见 `BrowseScrollJumps.tsx` / `lib/browseScrollJumps.ts`。
   * ⚠️ 2026-10-20 / 迁移 0019：字表**按行分组**时外层是一个 `<div>`（里面每组一个
   *    `<ul>`），不分组时仍是原来的那个 `<ul>` —— 所以这里用 `HTMLElement`
   *    （不是 `HTMLUListElement`），靠回调 ref 挂到两种容器上。
   */
  const hanziListRef = useRef<HTMLElement | null>(null);
  const wordListRef = useRef<HTMLUListElement | null>(null);
  /** 把字表容器登记给跳转按钮（`<ul>` 与 `<div>` 都能用）。 */
  const setHanziListElement = (element: HTMLElement | null) => {
    hanziListRef.current = element;
  };

  /**
   * **已生效**那套有没有条件 —— 决定"列不列表"。
   *
   * ⚠️ 这里用的是 `appliedFilter` 而不是 `filter`：用户在下拉里改了条件但**还没点
   *    「查询」**时，列表必须**保持上一次的结果不动**（用户原话"不要选一个条件就刷新一个"）。
   * ⚠️ 判据与查询的 `enabled` 是**同一个纯函数**（`lib/dictationFilters.ts`），
   *    所以"界面说不列"和"没发请求"不可能走岔。
   */
  const filterActive = isFilterActive(appliedFilter);

  /**
   * **按「在哪张表上」决定渲染哪一块**（2026-10-19）：
   *   · 识字表 / 写字表 ⇒ 只有字块（词语块**整个不渲染**）；
   *   · 词语表 ⇒ 只有词语块（字块**整个不渲染**）；
   *   · 全部表 ⇒ 两块都渲染（保持原样）。
   *
   * ⚠️ 计数与「新增生字 / 新增词语」都长在各自的卡片里，所以"只算当前那一块"
   *    与"按钮只在对应块出现"是这个判断的**自然结果**，不需要第二套开关。
   */
  const blocks = browseBlocks(appliedFilter);

  /** 查询条的状态与提示（禁用原因 / 未生效提醒，全是纯函数给的）。 */
  const queryState = describeBrowseQuery(filter, appliedFilter);

  /**
   * 回车 = 点「查询」（桌面端）。
   *
   * ⚠️ 只在筛选卡这一块监听：别的时候按回车（比如在列表里点拼音、在弹窗里填表）
   *    不该顺手查一次。
   */
  const handleFilterKeyDown = (event: { key: string; preventDefault: () => void }) => {
    if (!isBrowseQueryShortcut(event.key)) return;
    event.preventDefault();
    onQuery();
  };

  const hanziState = describeListState(hanzi, hanziTotal);
  const wordState = describeListState(words, wordsTotal);
  const lessonState = describeListState(lessons, lessonsTotal);

  /**
   * **全部条目，一次渲染**（2026-10-20 用户要求"有多少个就显示多少个"）。
   *
   * ⚠️ 这里**不再** `slice`：分页逻辑已删（连 `hanziShown` / `revealIndex` 一起）。
   *    屏幕外条目的渲染成本见本文件头「性能」那一段（刻意**不**用
   *    `content-visibility: auto`，理由是不让跳转落点变成估计值）。
   * ⚠️ 排序**不在这里做**：顺序是后端给的（sort_order, created_at, id 全序）。
   */
  const hanziItems = hanzi ?? [];
  const wordItems = words ?? [];

  /**
   * **已生效**那套筛选解析出来的那一册（出版社 + 年级 + 学期 → 课本）。
   *
   * ⚠️ 2026-10-18：筛选里不再有 `textbookId` —— "现在在看哪一册"要靠三格解析。
   *    `null` = 三格没齐 / 这一册不存在 / 课本列表没拿到（三种情况界面上的说法不同，
   *    见 `describeFilterVolumeGap` 与 `describeCreateTargetGap`）。
   * ⚠️ 用**已生效**那套：这一行说的是"列表里正在看的是哪一册"，
   *    不是"下拉里刚点了哪一版"。
   */
  const activeVolume = findFilterVolume(textbooks, appliedFilter);
  /**
   * **单表时按录入时的行分组**（2026-10-20 / 迁移 0019）。
   *
   * 三个条件同时成立才分组（判据全在 `browseLineGroups.ts`，有单测）：
   *   1. 已生效的筛选解析出**恰好一本书**（`activeVolume !== null`）；
   *   2. `listType` 是 `shizi` / `xiezi`（词语表 / 全部表都**不**分组）；
   *   3. 这次拿到的**每一张卡都有行号**（有一张是 `null` 就整块不分组）。
   *
   * `null` ⇒ 渲染与改造前**逐字相同**的连续网格（一个字节都不改）。
   */
  const hanziLineGroups = groupBrowseHanziByLine({
    volume: activeVolume,
    listType: appliedFilter.listType,
    items: hanziItems,
  });
  /** 分组时列表上方那句说明（`null` = 没分组 ⇒ 不渲染）。 */
  const hanziLineGroupNote = describeBrowseLineGroups(hanziLineGroups);
  /**
   * 「新增生字 / 新增词语」要写进的那一册能不能确定（`null` = 不能，按钮禁用）。
   *
   * ⚠️ 判据与页面里 `createHanzi` / `createWord` 用的是**同一个纯函数** +
   *    **同一套已生效筛选**：新增会写进那一册，拿待生效的值会让字落到还没查过的册里。
   */
  const createGap = describeCreateTargetGap(textbooks, appliedFilter);
  /**
   * 「出版社 + 年级 + 学期」这一册还没有课本时的那句人话（空串 = 不该说）。
   * ⚠️ 这不是错误态：组合没有对应的册是正常的数据状态，**不要**报错。
   */
  const volumeGap = describeFilterVolumeGap(textbooks, appliedFilter);
  /**
   * 课文下拉与「课本与课文」卡片共用的数据（按课本筛过的那些课）。
   *
   * ⚠️ 2026-10-17：**不能**再从这份数据里推年级选项 —— 没有课文时它就是空的，
   *    而那会让「年级」下拉只剩「全部年级」（用户报的那个 bug）。
   *    年级选项现在来自 `gradeSelectOptions()`（`GRADES` 全量）。
   */
  const lessonOptions = lessons ?? [];
  /**
   * 「课文」相关界面现在是否渲染（见 `lib/dictationLessonsUi.ts` 的开关）。
   * ⚠️ 关掉只影响渲染：数据模型、接口、导入解析器全都保留。
   */
  const lessonsVisible = lessonsUiEnabled();
  /** 这一屏要渲染哪些筛选下拉（隐藏课文时少一个，列数也跟着收）。 */
  const filterKeys = browseFilterKeys(lessonsVisible);

  /**
   * 每个筛选下拉的 JSX。**顺序与是否渲染由 `filterKeys` 决定**，
   * 所以隐藏「课文」时的布局不会错位（不会少一列还按 5 列排）。
   */
  const filterNodes: Record<BrowseFilterKey, ReactNode> = {
    /**
     * **出版社**（2026-10-18 用户要求：原来这一格是「课本版本」）。
     *
     * 用户原话："不要把'出版社''年级''学期'和'表'都集中在'课本版本'一个下拉框中，
     * 因为右边已经分别有'年级''学期'和'在哪张表上'了。因此，你把'课本版本'改成
     * '**出版社**'，下拉菜单也只显示'**出版社**'。"
     *
     * 所以这里**只列去重后的出版社**（`publisherSelectOptions`）：
     * ⛔ 不要带年级 / 学期 / 统计（那些右边各有一格，而且选项会重新变成"一册一行"）。
     * ⛔ 也不要再按 `textbookId` 记筛选：查询语义是「出版社 + 年级 + 学期」。
     */
    publisher: (
      <Select
        label="出版社"
        value={filter.publisher}
        onChange={(event) => onFilter({ publisher: event.target.value, lessonId: '' })}
      >
        {publisherSelectOptions(textbooks).map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    ),
    /**
     * 年级：选项 = **`GRADES` 全量**（12 个），「全部年级」在最前面。
     *
     * ⛔ 改造前这里是从课文列表里推的（`new Set(lessons.map(l => l.gradeCode))`），
     *    于是"这节课还没建课文"就等于"这个年级不存在" —— 用户导入的 280 个字
     *    没有课文，下拉里就只剩「全部年级」。⛔ 也不要改成"只列有数据的年级"：
     *    那会让人以为别的年级的字丢了。
     */
    grade: (
      <Select
        label="年级"
        value={filter.gradeCode}
        onChange={(event) => onFilter({ gradeCode: event.target.value })}
      >
        {gradeSelectOptions().map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    ),
    semester: (
      <Select
        label="学期"
        value={filter.semester}
        onChange={(event) => onFilter({ semester: event.target.value })}
      >
        <option value="">全部学期</option>
        <option value="上">上册</option>
        <option value="下">下册</option>
        <option value="不分">不分学期</option>
      </Select>
    ),
    /** 课文下拉：暂时隐藏（`DICTATION_LESSONS_UI_ENABLED=false` 时不在 `filterKeys` 里）。 */
    lesson: (
      <Select
        label="课文"
        value={filter.lessonId}
        onChange={(event) => onFilter({ lessonId: event.target.value })}
      >
        <option value="">全部课文</option>
        {lessonOptions.map((lesson) => (
          <option key={lesson.id} value={lesson.id}>
            {lesson.lessonLabel !== '' ? `${lesson.lessonLabel} ` : ''}
            {lesson.title}
          </option>
        ))}
      </Select>
    ),
    listType: (
      <Select
        label="在哪张表上"
        value={filter.listType}
        onChange={(event) => onFilter({ listType: event.target.value })}
        hint="同一个字可以同时在两张表上"
      >
        <option value="">全部表</option>
        <option value="shizi">识字表（会认）</option>
        <option value="xiezi">写字表（会写）</option>
        {/*
          ⚠️ 2026-10-19：用户报的 bug —— 这个下拉里**没有「词语表」**。
              值用后端既有枚举 `cibiao`（`shared/dictation/listing.ts` 的
              `LIST_TYPES`），**没有新造值**：服务层对它的语义是
              "词本身就来自课本词语表"（`word.from_textbook = true`），
              而字块在选它时整个不渲染。
          ⛔ 不要写成 `cibiao` 以外的值（例如 `ciyu` / `word`）——后端会 400。
        */}
        <option value="cibiao">词语表</option>
      </Select>
    ),
  };

  /**
   * 筛选卡片：**未选与已选两种状态都要它**，所以收成一个变量 ——
   * 两处 `return` 共用同一份 JSX，不产生"第二个筛选区"。
   *
   * ⚠️ 用 `<>…</>` 而不是把整块搬进一个子组件：筛选状态是页面持有的，
   *    这里只需要"同一段 JSX 用两次"，多一层组件只会多一层 props。
   * ⚠️ 回车监听挂在**这一块**（不是整页）：桌面端在筛选区按回车 = 点「查询」，
   *    在列表 / 弹窗里按回车不该顺手查一次。
   */
  const filterCard = (
    <>
      {/* ============================================ 筛选 */}
      <Card>
        <div onKeyDown={handleFilterKeyDown}>
          <CardHeader
            title="筛选"
            /*
              ⚠️ 2026-10-18：第一格从「课本版本」改成「出版社」，口径也要跟着改：
                 出版社 + 年级 + 学期 = 一本具体的册（右边各占一格，不再挤在一个下拉里）。
                 选出版社**不是必要条件**：留在「全部出版社」也能按年级 / 学期 / 表查。
            */
            description="出版社 + 年级 + 学期 = 一册；留在「全部出版社」也可以，那一格不用先选。"
          />
          <div
            className={cn(
              'grid grid-cols-2 gap-3 sm:grid-cols-3',
              browseFilterGridClass(filterKeys.length),
            )}
          >
            {filterKeys.map((key) => (
              <Fragment key={key}>{filterNodes[key]}</Fragment>
            ))}
          </div>
          {/*
            ⚠️ 2026-10-18：「出版社 + 年级 + 学期」这一册**还没有课本**时的一句人话。
               用户要求："列表空 + 一句人话（例如「人民教育出版社 · 小学一年级 · 下册
               还没有课本」），**不要报错**" —— 所以这里用中性色，不是错误态，
               也不是可点按钮（组合没有对应的册是数据状态，不是操作失败）。
            ⚠️ 三格没齐、或课本列表没拿到时**不说**（那两种情况另说，见句子的判据）。
          */}
          {volumeGap !== '' && (
            <p className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
              {volumeGap}
            </p>
          )}
          {/*
            「新增生字 / 新增词语」的落点说明（**只对管理员**；家长端没有那两个按钮）。
            ⚠️ 与按钮的 `disabled` / `title` 用的是**同一个** `createGap`：
               按钮灰着，旁边就有一句为什么（手机上 hover 不可用，不能只靠 title）。
            ⚠️ 这一册**不存在**时不再重复一遍（上面那句 `volumeGap` 已经说清了）。
            ⚠️ 只在 `filterActive` 时说明：没查过的时候列表与"新增"按钮根本不存在，
               这时先说"新增要写进具体的一册"是在解释一个看不见的按钮。
          */}
          {filterActive && canEdit && createGap !== '' && volumeGap === '' && (
            <p className="mt-2 text-xs leading-relaxed text-slate-500">
              新增生字 / 新增词语要写进具体的一册：{createGap}
            </p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-slate-600">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300"
                checked={filter.overriddenOnly}
                onChange={(event) => onFilter({ overriddenOnly: event.target.checked })}
              />
              只看人工改过拼音的（纠音清单）
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300"
                checked={filter.polyphonicOnly}
                onChange={(event) => onFilter({ polyphonicOnly: event.target.checked })}
              />
              {POLYPHONIC_FILTER_LABEL}
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300"
                checked={filter.fromTextbookOnly}
                onChange={(event) => onFilter({ fromTextbookOnly: event.target.checked })}
              />
              只看课本词语表里的词
            </label>
            <Select
              label=""
              aria-label="词语类别"
              className="w-40"
              value={filter.wordCategory}
              onChange={(event) => onFilter({ wordCategory: event.target.value })}
            >
              <option value="">词的全部类别</option>
              <option value="common">常用词</option>
              <option value="phrase">词语</option>
              <option value="idiom">成语</option>
            </Select>
          </div>

          {/*
            「只看多音字」的口径说明（用户 2026-10-18 要求）：勾选框旁边必须讲清
            "多音字是谁定的" —— 否则会以为系统按拼音库自动判断（那个口径把变调、
            古音、生僻音都算进来，280 个字里会标出 134 个）。
            ⛔ 不要把它并进下面那行规则提示里：那行讲的是"点了才查"，两件事。
          */}
          <p className="mt-2 text-xs leading-relaxed text-slate-500">{POLYPHONIC_FILTER_HINT}</p>

          {/*
            ⚠️ 这一行是**规则本身**，全页只此一处（点了才查 / 记在本机）。
            "不选就不查"**不再解释**（本轮按用户要求删掉那句禁用原因）：
            按钮灰着就是那个意思。⛔ 不要在这里补一句"先选条件"。
            口径与 `isFilterActive()` 逐字对应，别只改一边。
          */}
          <p className="mt-3 text-xs leading-relaxed text-slate-500">{BROWSE_FILTER_RULE_HINT}</p>

          {/*
            ============================================ 查询条

            「先选、再查询」的落点：改动只进**待生效**那套（上面几个下拉绑的就是它），
            只有点这里（或按回车）才把待生效那套升成已生效、才下发请求。

            ⚠️ 没有可查条件时按钮**禁用**，但**不解释为什么**（2026-10-18 本轮：用户
               圈红的那句禁用原因已整句删除；⛔ 不要换一句话补回来）。
            ⚠️ 有未生效改动时：按钮上亮一个小圆点 + 左侧琥珀色提示。
            ⚠️ 「重置」只清待生效那套，**不查**、不动已生效那套（列表保持上一次的结果）。
          */}
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3">
            {queryState.hint !== '' && (
              <p
                className={cn(
                  'min-w-0 rounded-lg px-2 py-1 text-xs leading-relaxed',
                  queryState.canQuery
                    ? 'bg-amber-50 text-amber-800'
                    : 'bg-slate-50 text-slate-600',
                )}
              >
                {queryState.hint}
              </p>
            )}
            <div className="ml-auto flex shrink-0 items-center gap-2">
              {/*
                ⚠️ 两个按钮都用 `md`（h-11 = 44px）：与上面的下拉一样高，手机上也好点
                   （`sm` 只有 36px，是这一屏最小的可点目标）。
              */}
              <Button
                size="md"
                variant="secondary"
                onClick={onResetFilter}
                title="只清掉待生效的条件，不会查询"
              >
                {BROWSE_RESET_BUTTON_LABEL}
              </Button>
              <Button
                size="md"
                onClick={onQuery}
                disabled={!queryState.canQuery}
                title={queryState.title}
              >
                {BROWSE_QUERY_BUTTON_LABEL}
                {queryState.pending && (
                  <span aria-hidden="true" className="ml-1 h-2 w-2 rounded-full bg-white" />
                )}
              </Button>
            </div>
          </div>

          {/* 课本列表拿不到时说清楚（不是"你还没有课本"） */}
          {textbooksMissing && (
            <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              没拿到课本列表（后端返回的字段名与预期不一致）—— 这不是"你还没有课本"。请刷新重试。
            </p>
          )}
        </div>
      </Card>
    </>
  );

  /**
   * —— **已生效**那套没有任何条件：**只**渲染筛选卡 ——
   *
   * ⚠️ 判据是 `appliedFilter`（不是 `filter`）：用户在下拉里选了东西但还没点
   *    「查询」时，列表要**保持上一次的结果不动**，不能因为条件有值就跳去列数据。
   * ⚠️ 这里直接 return，所以下面「生字」「词语」两张卡**根本不执行**：
   *    查询的 `enabled` 已经是 false（一个请求都没发），再把卡片画出来就是
   *    「共 — 个 + 这个筛选下还没有生字」，看起来像数据丢了。
   * ⚠️ 2026-10-18（本轮）：这里**只有筛选卡**（用户圈掉的那张引导卡已删）。
   *    ⛔ 不要补提示、不要补快捷入口 —— "没选条件"时看到筛选卡（含灰掉的「查询」）
   *    就是用户要的效果。
   * ⚠️ 家长端走的是**同一段代码**，没有第二套分支。
   */
  if (!filterActive) {
    return <div className="space-y-4">{filterCard}</div>;
  }

  /**
   * 一张字卡（**分组与不分组共用这一份**）。
   *
   * ⚠️ 卡片上的东西一个都没加：底色 / 已标音 / 点击开浮窗 / 跳转高亮 / 拼音
   *    全在 `HanziCard` 里，这里只决定它落在哪个 `<ul>` 里。
   * ⚠️ React key 与"同一个字互相跳转"的键**逐字相同**（`hanziRowKey()`）：
   *    迁移 0018 之后同一个字录了 N 次是 N 行，用 `id` 当 key 会撞车；没有
   *    `listingId` 的兜底行也必须靠 `rowIndex` 区分，否则 `Map.set` 后注册的
   *    覆盖先注册的 —— 点"第 2 个"会落到第 1 个（2026-10-21 用户报的 bug）。
   */
  const renderHanziCard = (item: HanziView, rowIndex: number) => {
    const cardKey = hanziRowKey(item, rowIndex);
    return (
      <HanziCard
        key={cardKey}
        item={item}
        cardRef={props.registerCard?.(cardKey)}
        highlighted={props.highlightedKey === cardKey}
        onOpen={() => props.onOpenHanzi(item, cardKey)}
      />
    );
  };

  return (
    <div className="space-y-4">
      {filterCard}

      {/*
        ============================================ 课本与课文（**暂时隐藏**）

        ⚠️ 2026-10-17 用户原话："暂时不需要录课文，可以先隐藏和关闭这个功能"。
           开关就是 `lib/dictationLessonsUi.ts` 里的 `DICTATION_LESSONS_UI_ENABLED`：
           改回 `true`，这一张卡原样回来（组件、接口、三张表一条都没删）。
           隐藏它不只是"用不上" —— 这一册没有课文时它就是一块空白卡片
           （截图里那句「这个筛选下还没有课文」），白占一屏的位置。
      */}
      {lessonsVisible && (
        <Card>
          <CardHeader
            title="课本与课文"
            description={
              activeVolume === null
                ? '出版社、年级学期、课文（课序）。'
                : `${activeVolume.name}：课文 ${countLabel(activeVolume.lessonCount)} 节、生字 ${countLabel(
                    activeVolume.hanziCount,
                  )} 个、词语 ${countLabel(activeVolume.wordCount)} 个。`
            }
            actionAlign="start"
            action={
              canEdit ? (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={props.onCreateLesson}
                  disabled={createGap !== ''}
                  title={createGap !== '' ? createGap : '新建课文'}
                  icon={<IconPlus size={16} />}
                >
                  新建课文
                </Button>
              ) : undefined
            }
          />

          {loading ? (
            <Skeleton className="h-16 w-full" />
          ) : lessonsMissing ? (
            <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              没拿到课文列表（后端返回的字段名与预期不一致）—— 这不是"还没有课文"。
            </p>
          ) : lessons === null || lessons.length === 0 ? (
            <EmptyState
              icon="📖"
              title="这个筛选下还没有课文"
              /*
                ⚠️ 只读模式（家长）**不能**说"可以手工新建 / 用批量导入" ——
                  这两件事都只有系统管理员能做。让他去找不存在的入口，
                  比什么都不说更糟（本项目明确反对"点了会失败"的入口）。
              */
              description={
                canEdit
                  ? '课文可以手工新建；也可以用「批量导入」粘一份课本目录，程序会自动建课。'
                  : '这套字表由站点管理员维护。没有这一册的课文时，可以先按现有的字词布置听写，或联系管理员补充。'
              }
            />
          ) : (
            <>
              <button
                type="button"
                onClick={() => setLessonsOpen((prev) => !prev)}
                className="flex w-full items-center gap-2 rounded-xl bg-slate-50 px-3 py-2 text-left text-sm font-medium text-slate-700 hover:bg-slate-100"
              >
                <IconChevronDown
                  size={16}
                  className={lessonsOpen ? 'rotate-180 transition-transform' : 'transition-transform'}
                />
                课文列表（{countLabel(lessonsTotal)} 节
                {lessonsTotal !== null && lessonsTotal > lessons.length
                  ? `，当前显示 ${lessons.length} 节`
                  : ''}
                ）
              </button>
              {lessonState.message !== '' && (
                <p className="mt-2 text-xs text-slate-500">{lessonState.message}</p>
              )}
              {lessonsOpen && (
                <ul className="mt-2 space-y-1.5">
                  {lessons.map((lesson) => (
                    <li
                      key={lesson.id}
                      className="flex flex-wrap items-start justify-between gap-2 rounded-xl border border-slate-200 px-3 py-2"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-slate-900">
                          {lesson.lessonLabel !== '' ? `${lesson.lessonLabel} ` : ''}
                          {lesson.title}
                        </p>
                        <p className="text-xs text-slate-500">
                          {lesson.textbookName ?? '（没拿到课本名）'} · {gradeLabel(lesson.gradeCode)} ·{' '}
                          {semesterLabel(lesson.semester)} · 课序 {lesson.lessonNo}
                          {lesson.unitNo !== null ? ` · 第 ${lesson.unitNo} 单元` : ''}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          /*
                            ⚠️ 2026-10-18：筛选里没有 `textbookId` 了 —— "只看这一课"
                               要顺手把这一课所属的那一册**翻译成出版社 + 年级 + 学期**。
                               （课文界面当前隐藏，但这段在开关打开时要能正确工作。）
                          */
                          onClick={() => {
                            const textbook = (textbooks ?? []).find(
                              (item) => item.id === lesson.textbookId,
                            );
                            onFilter({
                              lessonId: lesson.id,
                              ...(textbook === undefined
                                ? {}
                                : {
                                    publisher: publisherDisplayNameOf(textbook),
                                    gradeCode: textbook.gradeCode,
                                    semester: textbook.semester,
                                  }),
                            });
                          }}
                        >
                          只看这一课
                        </Button>
                        {canEdit && (
                          <>
                            <Button
                              size="icon"
                              variant="secondary"
                              aria-label={`编辑课文 ${lesson.title}`}
                              title="编辑课文"
                              onClick={() => props.onEditLesson(lesson)}
                              icon={<IconPencil size={18} />}
                            />
                            <Button
                              size="icon"
                              variant="danger"
                              aria-label={`删除课文 ${lesson.title}`}
                              title="删除课文（字与词本身保留）"
                              onClick={() => props.onDeleteLesson(lesson)}
                              icon={<IconTrash size={18} />}
                            />
                          </>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </Card>
      )}

      {/* ============================================ 生字（选中「词语表」时**整个不渲染**） */}
      {blocks.hanzi && (
      <Card>
        <CardHeader
          title="生字"
          /*
            ⚠️ 2026-10-18（本轮）：字卡改成**网格**（用户："录入汉字这个模块，显示
               汉字的方式比较好……把这一刻显示汉字的方法，替换掉浏览查询模块里
               显示汉字的方式"）。所以这里只讲两件事：**点卡片**、**底色是什么**。
            ⛔ 不要再写"点拼音纠音 / 点这一行的「组词」"：拼音与造词都在浮窗里了。
          */
          description={
            '课本后面的 1 类字（会写）与 2 类字（会认）。点任意一个字卡 → 弹出浮窗：' +
            '看每个读音的词、造词、标注多音字、改拼音、删除。' +
            '⚠️ 有**琥珀色底色**的是你确认标过的多音字（拼音库里的多读音不算）。'
          }
          actionAlign="start"
          action={
            <div className="flex basis-full flex-wrap items-center gap-2">
              <Badge tone={hanziTotal === null ? 'warn' : 'neutral'}>
                共 {countLabel(hanziTotal)} 个
              </Badge>
              {canEdit && (
                <Button
                  size="sm"
                  variant="secondary"
                  /*
                    ⚠️ 判据用**已生效**那本课本（`appliedFilter`，与页面里
                       `createHanzi` 用的同一个）：新增会**写进那本书**，
                       拿待生效的值会让字落到一本还没查过的书里。
                  */
                  disabled={createGap !== ''}
                  title={createGap !== '' ? createGap : '新增生字'}
                  onClick={props.onCreateHanzi}
                  icon={<IconPlus size={16} />}
                >
                  新增生字
                </Button>
              )}
            </div>
          }
        />

        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : hanziMissing || hanziState.state === 'unknown' ? (
          <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {hanziState.message !== ''
              ? hanziState.message
              : '没拿到生字列表 —— 这不是"一个生字都没有"。'}
          </p>
        ) : hanziState.state === 'mismatch' ? (
          <p className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {hanziState.message}
          </p>
        ) : hanzi !== null && hanzi.length === 0 ? (
          <EmptyState
            icon="✍️"
            title="这个筛选下还没有生字"
            description={
              /*
                ⚠️ 勾了「只看多音字（按你标注的）」而一个都没标注时，**不要**说
                   "用批量导入粘一份字表" —— 字表明明有 280 个，只是还没标注。
                   这是用户 2026-10-18 那条反馈（95 → 0）之后最容易踩的坑。
                ⚠️ 2026-10-18：这一册**不存在**时（出版社 + 年级 + 学期 没有对应的课本）
                   先说那一句人话（`volumeGap`），不要说"去导入 / 去手工新增" ——
                   新增按钮那时是禁用的，让人去找一个点不动的入口更糟。
              */
              volumeGap !== ''
                ? volumeGap
                : appliedFilter.polyphonicOnly
                  ? canEdit
                    ? '还没有字被标注为多音字 —— 点开一个字卡，在浮窗里点「标为多音字」，它就会出现在这里。'
                    : '还没有字被标注为多音字（多音字由管理员标注）。'
                  : canEdit
                    ? '用上面的「批量导入」粘一份字表，或者手工新增。'
                    : '站点还没有录入这一册的字表。换一个出版社 / 年级试试，或联系管理员补充。'
            }
          />
        ) : (
          <>
            {hanziState.message !== '' && (
              <p className="mb-2 text-xs text-slate-500">{hanziState.message}</p>
            )}
            {/*
              ============================================ 字卡网格

              ⚠️ 用户原话（2026-10-18）："录入汉字这个模块，显示汉字的方式比较好。
                 那你就把……这一刻显示汉字的方法，**替换掉浏览查询模块里显示汉字
                 的方式**。" —— 所以列数与「按书录入」那张网格完全一致：
                 手机 3 列 → 小屏 6 列 → 桌面 8 列。
              ⚠️ **不显示"属于哪张表"**（用户："我查的时候就是通过哪个表去查的"）；
                 筛选里的「在哪张表上」照旧保留（那是查的条件，不是卡片上的说明）。
              ⚠️ 框里**没有按钮**：全部操作在点开的浮窗里（见 `HanziCard` 的注释）。
            */}
            {/*
              ============================================ 字表上方：跳到表尾 ↓

              ⚠️ 用户 2026-10-20："在**字表的上面加一个按钮，直接跳转到表尾**"。
              ⚠️ **始终显示**（不按"超过一屏"才出现）：短列表上点它几乎不动，无害；
                 判"是否超过一屏"要量 DOM，首帧与服务端渲染都拿不到 —— 见
                 `BrowseScrollJumps.tsx` 的文件头。
            */}
            <div className="mb-2 flex justify-end">
              <BrowseScrollJumps listRef={hanziListRef} edge="bottom" />
            </div>
            {/*
              ============================================ 按录入时的行分组（2026-10-20）

              ⚠️ 只有三个条件同时成立才走分组（判据与文案在 `browseLineGroups.ts`，
                 有单测）：
                   1. 已生效的筛选解析出**恰好一本书**（出版社 + 年级 + 学期）；
                   2. `listType` 是识字表 / 写字表（词语表 / 全部表不分组）；
                   3. **每一张卡都有行号**（`listingLineNo`，迁移 0019）。
              ⚠️ 不分组时下面这一支**一个字都没改** —— 还是改造前的连续网格。
              ⚠️ 分组用的是**同一份** `groupByLine()`（导入预览那份）：标题
                 `第 N 行（X 个字）`、浅灰底 + 边框、`HANZI_GRID_CLASS` 3/6/8 列，
                 与预览里看到的一模一样。
            */}
            {hanziLineGroupNote !== null && hanziLineGroups !== null ? (
              <div ref={setHanziListElement} data-browse-line-groups="true">
                <p className="mb-2 text-xs leading-relaxed text-slate-500">{hanziLineGroupNote}</p>
                <div className="space-y-4">
                  {hanziLineGroups.map((group) => (
                    <section
                      key={group.key}
                      data-line-group={group.line}
                      aria-label={group.title}
                      className="rounded-xl border border-slate-200 bg-slate-50/60 px-2 py-2"
                    >
                      <h4 className="mb-1.5 px-0.5 text-sm font-semibold text-slate-800">
                        {group.title}
                      </h4>
                      <ul className={HANZI_GRID_CLASS}>
                        {group.positions.map((position) =>
                          renderHanziCard(position.item, position.rowIndex),
                        )}
                      </ul>
                    </section>
                  ))}
                </div>
              </div>
            ) : (
              <ul ref={setHanziListElement} className={HANZI_GRID_CLASS}>
                {hanziItems.map((item, rowIndex) => renderHanziCard(item, rowIndex))}
              </ul>
            )}
            {/*
              底部只剩一句总数「共 N 个」+「跳到表头 ↑」。
              ⚠️ 2026-10-20：分页那行（「已显示 100 / 共 280 个」）与「再显示 100 个」
                 按钮**已删**；所有条目都在上面一次渲染完了。
              ⚠️ `available` 传的是**这次取到的条数**：超过后端上限 20000 时，
                 底部会说"服务器这次只返回了 N 条"，而不是假装已经全部显示。
            */}
            <BrowseListFooter
              total={hanziTotal}
              available={hanziItems.length}
              listRef={hanziListRef}
            />
          </>
        )}
      </Card>
      )}

      {/* ============================================ 词语（选中识字表 / 写字表时**整个不渲染**） */}
      {blocks.words && (
      <Card>
        <CardHeader
          title="词语（含成语）"
          description="整词拼音是「按词定音」的唯一入口：银行要能改成 yín háng。"
          actionAlign="start"
          action={
            <div className="flex basis-full flex-wrap items-center gap-2">
              <Badge tone={wordsTotal === null ? 'warn' : 'neutral'}>
                共 {countLabel(wordsTotal)} 个
              </Badge>
              {canEdit && (
                <Button
                  size="sm"
                  variant="secondary"
                  /** ⚠️ 同上：判据用**已生效**那本课本。 */
                  disabled={createGap !== ''}
                  title={createGap !== '' ? createGap : '新增词语'}
                  onClick={props.onCreateWord}
                  icon={<IconPlus size={16} />}
                >
                  新增词语
                </Button>
              )}
            </div>
          }
        />

        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : wordsMissing || wordState.state === 'unknown' ? (
          <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {wordState.message !== '' ? wordState.message : '没拿到词语列表 —— 这不是"一个词都没有"。'}
          </p>
        ) : wordState.state === 'mismatch' ? (
          <p className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {wordState.message}
          </p>
        ) : words !== null && words.length === 0 ? (
          <EmptyState
            icon="📖"
            title="这个筛选下还没有词语"
            description={
              /* ⚠️ 同上：这一册不存在时先说那句人话（不是说"去导入 / 去手工新增"）。 */
              volumeGap !== ''
                ? volumeGap
                : canEdit
                  ? '课本后面的词语表可以整块粘进「批量导入」；也可以手工新增。'
                  : '站点还没有录入这一册的词语表。换一个出版社 / 年级试试，或联系管理员补充。'
            }
          />
        ) : (
          <>
            {wordState.message !== '' && (
              <p className="mb-2 text-xs text-slate-500">{wordState.message}</p>
            )}
            {/*
              ============================================ 词表上方：跳到表尾 ↓
              ⚠️ 与字块同一条口径（用户说的是"字表"，词表也长在同一屏、同样会长到几百行，
                 所以这颗按钮一起给；位置与文案逐字相同）。
            */}
            <div className="mb-2 flex justify-end">
              <BrowseScrollJumps listRef={wordListRef} edge="bottom" />
            </div>
            <ul ref={wordListRef} className="space-y-2">
              {wordItems.map((item) => (
                <ItemRow
                  key={item.id}
                  primary={item.wordText}
                  badges={
                    <>
                      <Badge tone={item.category === 'idiom' ? 'success' : 'neutral'}>
                        {wordCategoryLabel(item.category)}
                      </Badge>
                      {item.fromTextbook && <Badge tone="brand">课本词语表</Badge>}
                      {item.gradeCode !== null && <Badge>{gradeLabel(item.gradeCode)}</Badge>}
                      {item.semester !== null && <Badge>{semesterLabel(item.semester)}</Badge>}
                    </>
                  }
                  pinyinNode={
                    <PinyinCell
                      kind="word"
                      text={item.wordText}
                      pinyin={item.pinyin}
                      pinyinAuto={item.pinyinAuto}
                      overridden={item.pinyinOverridden}
                      editing={props.pinyinEdit?.kind === 'word' && props.pinyinEdit.id === item.id}
                      saving={props.pinyinSaving}
                      error={props.pinyinError}
                      onStartEdit={() => props.onStartPinyin('word', item.id)}
                      onSave={(value) => props.onSavePinyin('word', item, value)}
                      onCancel={props.onCancelPinyin}
                    />
                  }
                  meta={item.note !== '' ? `备注：${item.note}` : ''}
                  onEdit={() => props.onEditItem('word', item)}
                  onDelete={() => props.onDeleteItem('word', item)}
                />
              ))}
            </ul>
            {/* 与字块同一条口径：只报总数（「共 N 个」），不再有分页。 */}
            <BrowseListFooter
              total={wordsTotal}
              available={wordItems.length}
              listRef={wordListRef}
            />
          </>
        )}
      </Card>
      )}
    </div>
  );
}
